/**
 * TenantContext.jsx — Caller's active tenant context + switcher logic.
 *
 * Hydrates from `GET /api/tenants/me/memberships` on mount, mirrors the
 * active tenant id into `localStorage['ariaone-tenant-id']` so every
 * subsequent httpClient call auto-injects the X-Tenant-Id header.
 *
 * Consumers:
 *   const { memberships, activeTenant, role, switchTenant, refresh,
 *           isPlatformAdmin, loading } = useTenant();
 */
import React, { createContext, useContext, useEffect, useState, useCallback } from 'react';
import * as tenantApi from '../lib/api/tenantApi';

const TenantCtx = createContext(null);
const STORAGE_KEY = 'ariaone-tenant-id';

export function TenantProvider({ children }) {
  const [memberships, setMemberships] = useState([]);
  const [activeId, setActiveId] = useState(() => localStorage.getItem(STORAGE_KEY) || null);
  const [isPlatformAdmin, setIsPlatformAdmin] = useState(false);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  // Platform-admin impersonation overlay — when set, activeTenant is
  // derived from this shim instead of the caller's membership list so
  // the UI can render the target tenant's data while keeping a clear
  // "you are viewing as" banner.
  const [impersonation, setImpersonation] = useState(() => {
    try {
      const raw = localStorage.getItem('ariaone-impersonation');
      return raw ? JSON.parse(raw) : null;
    } catch {
      return null;
    }
  });

  const refresh = useCallback(async () => {
    setLoading(true);
    setError(null);
    const r = await tenantApi.listMyMemberships();
    if (r.ok) {
      const list = r.data?.memberships || [];
      setMemberships(list);
      setIsPlatformAdmin(!!r.data?.is_platform_admin);
      // Validate/seed the active tenant.
      const stored = localStorage.getItem(STORAGE_KEY);
      const valid = list.find((m) => m.tenant_id === stored);
      if (valid) {
        setActiveId(stored);
      } else if (list.length > 0) {
        setActiveId(list[0].tenant_id);
        localStorage.setItem(STORAGE_KEY, list[0].tenant_id);
      } else {
        setActiveId(null);
        localStorage.removeItem(STORAGE_KEY);
      }
    } else {
      // 401 on a logged-out user is not an error for this page.
      if (r.error?.status !== 401) setError(r.error?.message || 'Failed to load memberships');
      setMemberships([]);
      setIsPlatformAdmin(false);
    }
    setLoading(false);
  }, []);

  useEffect(() => { refresh(); }, [refresh]);

  const switchTenant = useCallback((tenantId) => {
    if (!tenantId) {
      localStorage.removeItem(STORAGE_KEY);
      setActiveId(null);
      return;
    }
    localStorage.setItem(STORAGE_KEY, tenantId);
    setActiveId(tenantId);
  }, []);

  const beginImpersonation = useCallback((tenantId, tenantName, tenantSlug) => {
    const shim = { tenant_id: tenantId, tenant_name: tenantName, tenant_slug: tenantSlug };
    localStorage.setItem('ariaone-impersonation', JSON.stringify(shim));
    localStorage.setItem(STORAGE_KEY, tenantId);
    setImpersonation(shim);
    setActiveId(tenantId);
  }, []);

  const endImpersonation = useCallback(() => {
    localStorage.removeItem('ariaone-impersonation');
    setImpersonation(null);
    // Reset to the caller's real first active membership (if any).
    const real = memberships[0]?.tenant_id || null;
    if (real) {
      localStorage.setItem(STORAGE_KEY, real);
      setActiveId(real);
    } else {
      localStorage.removeItem(STORAGE_KEY);
      setActiveId(null);
    }
  }, [memberships]);

  // When impersonating, surface the shim as the active tenant — this
  // makes every tenant-scoped call + the admin page + topbar banner
  // render against the impersonated tenant without touching any of the
  // 27 existing tenant-aware surfaces.
  const realActive = memberships.find((m) => m.tenant_id === activeId) || null;
  const activeTenant = impersonation
    ? {
        tenant_id:   impersonation.tenant_id,
        tenant_name: impersonation.tenant_name,
        tenant_slug: impersonation.tenant_slug,
        role:        'owner',
        _impersonated: true,
      }
    : realActive;
  const role = activeTenant?.role || null;

  const value = {
    memberships,
    activeTenant,
    activeId,
    role,
    isPlatformAdmin,
    loading,
    error,
    switchTenant,
    refresh,
    impersonation,
    isImpersonating: !!impersonation,
    beginImpersonation,
    endImpersonation,
  };

  return <TenantCtx.Provider value={value}>{children}</TenantCtx.Provider>;
}

export function useTenant() {
  const ctx = useContext(TenantCtx);
  if (!ctx) throw new Error('useTenant() must be used inside <TenantProvider>');
  return ctx;
}

// ── Pure RBAC helper shared with server contract ──────────────────────
const ROLE_ORDER = ['viewer', 'member', 'admin', 'owner'];
export const roleLevel = (r) => (r ? ROLE_ORDER.indexOf(r) : -1);
export const roleMeets = (required, actual) => roleLevel(actual) >= roleLevel(required);
